import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { HOTELS_QUERY_KEY, listHotels, type Hotel } from '../features/hotels/api.js';
import { createPlan, type CreateMonthlyPlanBody } from '../features/plans/api.js';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card.js';
import { Field, FormActions } from '../components/ui/form.js';
import { Input } from '../components/ui/input.js';
import { Textarea } from '../components/ui/textarea.js';
import { Select } from '../components/ui/select.js';
import { Button } from '../components/ui/button.js';
import { TagInput } from '../components/ui/tag-input.js';

const QuerySchema = z.object({
  hotelId: z.string().uuid().optional(),
  /** Prefill the period to this calendar month when arriving from the
   * plan page's "create plan for this month" empty state. */
  year: z.coerce.number().int().min(2024).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
});

function NewPlanPage() {
  const search = Route.useSearch();
  const hotels = useQuery<Hotel[]>({ queryKey: HOTELS_QUERY_KEY, queryFn: listHotels });
  const navigate = useNavigate();
  const today = new Date();
  const [hotelId, setHotelId] = useState(search.hotelId ?? '');
  // Default the period to "current month, full month". User can shorten or
  // extend either bound. year+month are derived from startDate at submit.
  // When ?year=&month= are supplied (e.g. from the plan page's empty state
  // or the hotel year calendar), prefill that month instead of "today".
  const prefillYear = search.year ?? today.getUTCFullYear();
  const prefillMonth = search.month ?? today.getUTCMonth() + 1;
  const prefillDate = new Date(Date.UTC(prefillYear, prefillMonth - 1, 1));
  const firstOfMonth = `${prefillYear}-${String(prefillMonth).padStart(2, '0')}-01`;
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(lastOfMonthIso(prefillDate));
  const [brief, setBrief] = useState('');
  const [focusKeywords, setFocusKeywords] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      // year+month is the "primary" month for the period — we anchor it to
      // the startDate. The backend uses it for the unique (hotel, year, month)
      // constraint, so two periods starting in the same month for one hotel
      // can't coexist (intentional — avoids ambiguous URL identity).
      const year = Number(startDate.slice(0, 4));
      const month = Number(startDate.slice(5, 7));
      const body: CreateMonthlyPlanBody = {
        hotelId,
        year,
        month,
        startDate,
        endDate,
        focusKeywords,
      };
      if (brief) body.brief = brief;
      return createPlan(body);
    },
    onSuccess: (p) =>
      void navigate({
        to: '/app/plans/$hotelId/$year/$month',
        params: { hotelId: p.hotelId, year: String(p.year), month: String(p.month) },
      }),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="mx-auto max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Új havi terv</CardTitle>
          <CardDescription>
            Hotel + év + hónap pár egyedi. Brief később is szerkeszthető.
          </CardDescription>
        </CardHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate();
          }}
          className="space-y-4"
        >
          <Field label="Hotel" htmlFor="hotel">
            <Select
              id="hotel"
              value={hotelId}
              required
              onChange={(e) => setHotelId(e.target.value)}
            >
              <option value="">— válassz —</option>
              {hotels.data?.map((h) => (
                <option key={h.id} value={h.id} disabled={!h.isActive}>
                  {h.name}
                  {!h.isActive ? ' (inaktív)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Időszak kezdete" htmlFor="start-date">
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </Field>
            <Field
              label="Időszak vége"
              htmlFor="end-date"
              hint="Egy hónapnál rövidebb vagy hosszabb tartomány is engedélyezett."
            >
              <Input
                id="end-date"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </Field>
          </div>
          <Field
            label="Brief"
            htmlFor="brief"
            hint="Manager havi promptja (pl. wellness fókusz, palacsinta fesztivál)"
          >
            <Textarea id="brief" value={brief} onChange={(e) => setBrief(e.target.value)} />
          </Field>
          <Field label="Fókusz kulcsszavak" htmlFor="keywords">
            <TagInput value={focusKeywords} onChange={setFocusKeywords} />
          </Field>
          {error && <p className="text-body text-red-700">{error}</p>}
          <FormActions>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void navigate({ to: '/app/plans' })}
            >
              Mégse
            </Button>
            <Button type="submit" disabled={!hotelId || create.isPending}>
              {create.isPending ? 'Mentés…' : 'Létrehozás'}
            </Button>
          </FormActions>
        </form>
      </Card>
    </div>
  );
}

function lastOfMonthIso(d: Date): string {
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  return last.toISOString().slice(0, 10);
}

export const Route = createFileRoute('/app/plans/new')({
  validateSearch: (s) => QuerySchema.parse(s),
  component: NewPlanPage,
});
